/**
 * Layer spine shared by every overlay primitive.
 *
 * A layer is an element that floats above the page: a dialog, a popover, a
 * menu, a tooltip. Five behaviours belong to the layer rather than to any one
 * pattern, and writing them per component is how overlays drift apart:
 *
 * 1. **Portal** — the content moves to `[data-hydrogen-portal]` on `body`
 *    while open so no ancestor `overflow`, `transform` or `z-index` can clip
 *    or bury it, and returns to where the markup put it when the layer goes.
 * 2. **Stack** — layers register in open order. Escape and outside-pointer
 *    dismissal only ever act on the topmost layer, so a menu inside a dialog
 *    closes before the dialog does.
 * 3. **Modality** — a modal layer traps Tab, moves focus in, returns it to the
 *    opener on close, locks page scroll (ref-counted, scrollbar-compensated)
 *    and makes everything beneath it `inert`.
 * 4. **Dismissal** — Escape, pointerdown outside, and focus leaving, each
 *    individually switchable, reported through one `onDismiss(reason)`.
 * 5. **Teardown** — `destroy()` undoes all of the above exactly once.
 *
 * The layer never decides *whether* it is open. The primitive owns open state
 * and calls {@link mountLayer} on open and `destroy()` on close; `onDismiss`
 * is a request, not an action.
 */
import { FOCUSABLE_SELECTOR } from 'hydrogen/focus-trap';
const stack = [];
let listening = false;
const PORTAL_ATTRIBUTE = 'data-hydrogen-portal';
/** The shared portal root, created on first use and reused afterwards. */
export function portalRoot(doc = document) {
    const existing = doc.body.querySelector(`:scope > [${PORTAL_ATTRIBUTE}]`);
    if (existing)
        return existing;
    const root = doc.createElement('div');
    root.setAttribute(PORTAL_ATTRIBUTE, '');
    doc.body.appendChild(root);
    return root;
}
/** Number of live layers. Exposed for teardown assertions. */
export function layerCount() {
    return stack.length;
}
function topEntry() {
    return stack[stack.length - 1];
}
function containsTarget(entry, target) {
    if (entry.options.content.contains(target))
        return true;
    return entry.options.isInside?.(target) ?? false;
}
/**
 * A nested layer is inside its parent for dismissal purposes. A pointerdown in
 * a submenu portalled elsewhere must not read as "outside" the menu that owns
 * it, so any entry above `entry` counts as inside.
 */
function isInsideStackAbove(entry, target) {
    const index = stack.indexOf(entry);
    for (let i = index + 1; i < stack.length; i += 1) {
        const above = stack[i];
        if (above && containsTarget(above, target))
            return true;
    }
    return false;
}
function onKeydown(event) {
    const entry = topEntry();
    if (!entry)
        return;
    const { options } = entry;
    if (event.key === 'Escape' && options.dismissOnEscape !== false) {
        if (event.defaultPrevented)
            return;
        event.stopPropagation();
        options.onDismiss?.('escape', event);
        return;
    }
    if (event.key === 'Tab' && (options.trapFocus ?? options.modal ?? false)) {
        trapTab(entry, event);
    }
}
function focusablesIn(content) {
    return Array.from(content.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => !element.hasAttribute('inert') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.closest('[hidden]') === null);
}
function trapTab(entry, event) {
    const { content } = entry.options;
    const focusable = focusablesIn(content);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) {
        event.preventDefault();
        content.focus();
        return;
    }
    const active = document.activeElement;
    if (!active || !content.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
    }
    if (event.shiftKey && (active === first || active === content)) {
        event.preventDefault();
        last.focus();
    }
    else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
    }
}
function onPointerDown(event) {
    const entry = topEntry();
    if (!entry || entry.options.dismissOnOutsidePointer === false)
        return;
    const target = event.target;
    if (!(target instanceof Node))
        return;
    if (containsTarget(entry, target) || isInsideStackAbove(entry, target))
        return;
    entry.options.onDismiss?.('outside', event);
}
function onFocusIn(event) {
    const entry = topEntry();
    if (!entry)
        return;
    const { options } = entry;
    const target = event.target;
    if (!(target instanceof Node))
        return;
    if (containsTarget(entry, target))
        return;
    if (options.trapFocus ?? options.modal ?? false) {
        // Focus escaped a trap by a route other than Tab (a click on something
        // not yet inert, a programmatic focus): pull it back.
        const first = focusablesIn(options.content)[0] ?? options.content;
        first.focus();
        return;
    }
    if (options.dismissOnFocusOutside)
        options.onDismiss?.('focus-outside', event);
}
function listen() {
    if (listening)
        return;
    listening = true;
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn, true);
}
function unlisten() {
    if (!listening || stack.length > 0)
        return;
    listening = false;
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('focusin', onFocusIn, true);
}
// --- scroll lock (ref-counted, scrollbar compensated) ------------------------
let scrollLocks = 0;
let savedOverflow = '';
let savedPaddingRight = '';
function lockScroll() {
    const body = document.body;
    if (scrollLocks === 0) {
        const root = document.documentElement;
        const scrollbar = Math.max(0, (globalThis.innerWidth ?? 0) - root.clientWidth);
        savedOverflow = body.style.overflow;
        savedPaddingRight = body.style.paddingRight;
        if (scrollbar > 0) {
            const current = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
            body.style.paddingRight = `${current + scrollbar}px`;
        }
        body.style.overflow = 'hidden';
        body.setAttribute('data-scroll-locked', '');
    }
    scrollLocks += 1;
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        scrollLocks -= 1;
        if (scrollLocks === 0) {
            body.style.overflow = savedOverflow;
            body.style.paddingRight = savedPaddingRight;
            body.removeAttribute('data-scroll-locked');
        }
    };
}
/** Current scroll-lock depth. Exposed for teardown assertions. */
export function scrollLockCount() {
    return scrollLocks;
}
// --- inert (ref-counted per element) ----------------------------------------
const inertCounts = new WeakMap();
function makeInert(element) {
    const record = inertCounts.get(element) ?? {
        count: 0,
        hadInert: element.hasAttribute('inert'),
        hadHidden: element.getAttribute('aria-hidden'),
    };
    record.count += 1;
    inertCounts.set(element, record);
    element.setAttribute('inert', '');
    element.setAttribute('aria-hidden', 'true');
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        record.count -= 1;
        if (record.count > 0)
            return;
        inertCounts.delete(element);
        if (!record.hadInert)
            element.removeAttribute('inert');
        if (record.hadHidden === null)
            element.removeAttribute('aria-hidden');
        else
            element.setAttribute('aria-hidden', record.hadHidden);
    };
}
function inertBeneath(root, wrapper) {
    const releases = [];
    const keep = new Set([wrapper]);
    for (let node = wrapper; node && node !== document.body; node = node.parentElement) {
        keep.add(node);
    }
    for (const child of Array.from(document.body.children)) {
        if (child === root)
            continue;
        if (child.hasAttribute(PORTAL_ATTRIBUTE))
            continue;
        if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE')
            continue;
        if (!keep.has(child))
            releases.push(makeInert(child));
    }
    for (const sibling of Array.from(root.children)) {
        if (!keep.has(sibling))
            releases.push(makeInert(sibling));
    }
    return releases;
}
// --- mount ------------------------------------------------------------------
function resolveInitialFocus(options) {
    const wantsTrap = options.trapFocus ?? options.modal ?? false;
    const choice = options.initialFocus ?? (wantsTrap ? 'first' : false);
    if (choice === false)
        return null;
    if (choice === 'content')
        return options.content;
    if (choice === 'first') {
        const marked = options.content.querySelector('[autofocus], [data-autofocus]');
        return marked ?? focusablesIn(options.content)[0] ?? options.content;
    }
    return choice;
}
/**
 * Mounts `content` as a layer: portals it, pushes it on the stack and applies
 * modality and dismissal. Call `destroy()` to undo everything.
 */
export function mountLayer(options) {
    const { content } = options;
    const opener = options.returnFocusTo ?? document.activeElement;
    const container = options.container ?? portalRoot(content.ownerDocument);
    const marker = content.parentNode ? document.createComment('hydrogen-layer') : null;
    if (marker && content.parentNode)
        content.parentNode.insertBefore(marker, content);
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-hydrogen-layer', '');
    wrapper.appendChild(content);
    container.appendChild(wrapper);
    const entry = {
        layer: {
            content,
            get isTop() {
                return topEntry() === entry;
            },
            destroy: () => destroy(),
        },
        options,
        wrapper,
        marker,
        restore: [],
        destroyed: false,
    };
    if (options.modal) {
        entry.restore.push(lockScroll());
        entry.restore.push(...inertBeneath(container, wrapper));
    }
    stack.push(entry);
    listen();
    const focusTarget = resolveInitialFocus(options);
    if (focusTarget) {
        if (focusTarget === content && !content.hasAttribute('tabindex')) {
            content.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
    }
    function destroy() {
        if (entry.destroyed)
            return;
        entry.destroyed = true;
        const index = stack.indexOf(entry);
        if (index !== -1)
            stack.splice(index, 1);
        for (const release of entry.restore.splice(0))
            release();
        if (entry.marker?.parentNode) {
            entry.marker.parentNode.insertBefore(content, entry.marker);
            entry.marker.remove();
        }
        else {
            content.remove();
        }
        wrapper.remove();
        unlisten();
        if (options.restoreFocus !== false && opener?.isConnected) {
            opener.focus({ preventScroll: true });
        }
    }
    return entry.layer;
}
